import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Plus, Search, FolderOpen, Eye, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { UpgradeModal } from '../components/UpgradeModal';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

export default function PAFsPage() {
  const { apiCall, user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const navigate = useNavigate();
  const [pafs, setPafs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [creating, setCreating] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [planUsage, setPlanUsage] = useState({});
  const canEdit = user?.role !== 'viewer';

  const fetchPAFs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (selectedCompanyId) params.set('company_id', selectedCompanyId);
      const res = await apiCall('get', `/pafs?${params}`);
      setPafs(res.data.pafs);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load PAFs');
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, search, statusFilter, selectedCompanyId]);

  useEffect(() => { fetchPAFs(); }, [fetchPAFs]);

  const loadEmployees = async () => {
    try {
      const res = await apiCall('get', '/employees?limit=500');
      setEmployees(res.data.employees);
    } catch {}
  };

  const handleCreate = async () => {
    if (!selectedEmployee) return;
    setCreating(true);
    try {
      await apiCall('post', '/pafs', { employee_id: selectedEmployee });
      toast.success('PAF created');
      setShowCreate(false);
      setSelectedEmployee('');
      fetchPAFs();
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('limit')) {
        try {
          const res = await apiCall('get', '/plan/usage');
          setPlanUsage(res.data);
        } catch {}
        setShowUpgrade(true);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to create PAF');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PAF and all its documents?')) return;
    try {
      await apiCall('delete', `/pafs/${id}`);
      toast.success('PAF deleted');
      fetchPAFs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      compliant: 'bg-[#ECFDF5] text-[#059669]',
      warning: 'bg-[#FFFBEB] text-[#D97706]',
      non_compliant: 'bg-[#FEF2F2] text-[#DC2626]',
    };
    const labels = { compliant: 'Compliant', warning: 'Warning', non_compliant: 'Non-Compliant' };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.non_compliant}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div data-testid="pafs-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Public Access Files</h1>
            <p className="text-[#64748B]">Manage PAF compliance documents for each H-1B, H-1B1 & E-3 employee</p>
          </div>
          {canEdit && (
            <Button
              data-testid="create-paf-btn"
              onClick={() => { loadEmployees(); setShowCreate(true); }}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-full px-6 font-medium"
            >
              <Plus className="w-4 h-4 mr-2" /> Create PAF
            </Button>
          )}
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.05 }} className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input
            data-testid="search-pafs"
            placeholder="Search by employee name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 rounded-xl border-gray-200 bg-gray-50/50 h-11"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48 rounded-xl border-gray-200 h-11" data-testid="filter-paf-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="compliant">Compliant</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="non_compliant">Non-Compliant</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Table */}
      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-white rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : pafs.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="w-12 h-12 text-[#64748B] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#64748B]">No PAFs found</p>
            {canEdit && (
              <Button onClick={() => { loadEmployees(); setShowCreate(true); }}
                className="mt-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-full px-6"
              >
                Create your first PAF
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-indigo-100/50">
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Employee</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Completion</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Created</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pafs.map((paf) => (
                  <TableRow key={paf.id} className="border-b border-indigo-100/50 hover:bg-[#F8FAFF]/50 transition-colors cursor-pointer"
                    data-testid={`paf-row-${paf.id}`}
                    onClick={() => navigate(`/pafs/${paf.id}`)}
                  >
                    <TableCell className="font-medium text-[#0F172A]">
                      {paf.employee ? `${paf.employee.first_name} ${paf.employee.last_name}` : 'Unknown'}
                    </TableCell>
                    <TableCell>{statusBadge(paf.compliance_status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 w-40">
                        <Progress value={paf.completion_percentage} className="h-2 bg-[#F8FAFF] flex-1" />
                        <span className="text-sm text-[#64748B] w-10 text-right">{paf.completion_percentage}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#64748B]">{new Date(paf.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => navigate(`/pafs/${paf.id}`)} className="p-2 rounded-lg hover:bg-[#F8FAFF] text-[#64748B] hover:text-[#4F46E5] transition-colors" data-testid={`view-paf-${paf.id}`}>
                          <Eye className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        {user?.role === 'admin' && (
                          <button onClick={() => handleDelete(paf.id)} className="p-2 rounded-lg hover:bg-[#FEF2F2] text-[#64748B] hover:text-[#EF4444] transition-colors" data-testid={`delete-paf-${paf.id}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > 20 && (
              <div className="flex items-center justify-between p-4 border-t border-indigo-100/50">
                <span className="text-sm text-[#64748B]">Showing {(page-1)*20+1}–{Math.min(page*20, total)} of {total}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p-1)} className="rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={page*20 >= total} onClick={() => setPage(p => p+1)} className="rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="create-paf-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#0F172A] tracking-tight">Create PAF</DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">Create a Public Access File for an H-1B, H-1B1, or E-3 employee</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-[#64748B] mb-4">Select an employee to create a Public Access File for:</p>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-full rounded-xl border-gray-200 h-11" data-testid="select-paf-employee">
                <SelectValue placeholder="Choose an employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} — {emp.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full px-6">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !selectedEmployee}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-full px-6" data-testid="confirm-create-paf"
            >
              {creating ? 'Creating...' : 'Create PAF'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        limitType="paf"
        currentPlan={planUsage.account_type || 'free'}
        usage={planUsage}
      />
    </div>
  );
}
